import { Box2, Box3, Vector2, Vector2Tuple, Vector3 } from 'three';

import { FEnsembleTypesEnum } from '@spazious/dollhouse-shallow-graph';

import {
  FEnsembleCabaretSchema,
  FEnsembleCabaretSchemaFields,
  FEnsembleCabaretValues,
} from '../fensembles/index.js';
import { Schema, SchemaValues } from '../schema/index.js';
import { ChairsOrientation, FInstanceEnsembleNode, FInstanceNode } from '../types/index.js';
import { angleForChairsOrientation } from '../utils/angleForChairsOrientation.js';

import {
  FArrangementGrid,
  FArrangementGridSchemaFields,
  FArrangementGridValues,
} from './FArrangementGrid.js';

export type FArrangementCabaretSchemaFields = FArrangementGridSchemaFields &
  FEnsembleCabaretSchemaFields;

export interface FArrangementCabaretSchema extends Schema {
  fields: FArrangementCabaretSchemaFields;
}

export type FArrangementCabaretAttributes = Partial<SchemaValues<FArrangementCabaretSchemaFields>> &
  FArrangementGridValues &
  FEnsembleCabaretValues;

const TAU = Math.PI * 2;
const bbox = new Box3();
const _box = new Box2();
const _point = new Vector2();
const _size = new Vector2();

const pointAtAngle = (radius: number, angle: number): Vector2 => {
  return _point.set(radius * Math.cos(angle), radius * Math.sin(angle));
};

const isAngleInRange = (angle: number, startAngle: number, endAngle: number): boolean => {
  if (startAngle < endAngle) {
    return startAngle <= angle && angle <= endAngle;
  } else {
    return angle >= startAngle || angle <= endAngle;
  }
};

const cabaretBoundingBox = (
  minRadius: number,
  maxRadius: number,
  startAngle: number,
  endAngle: number
): Box2 => {
  startAngle = startAngle % TAU;
  endAngle = endAngle % TAU;

  _box.makeEmpty();
  // start and end edges
  _box.expandByPoint(pointAtAngle(maxRadius, startAngle));
  _box.expandByPoint(pointAtAngle(maxRadius, endAngle));

  for (const angle of [0, TAU * 0.25, TAU * 0.5, TAU * 0.75]) {
    if (isAngleInRange(angle, startAngle, endAngle)) {
      _box.expandByPoint(pointAtAngle(maxRadius, angle)); // point where chairs intersect axis
    } else {
      _box.expandByPoint(pointAtAngle(minRadius, angle)); // point where table intersect axis
    }
  }

  return _box;
};

const getTablePosition = (
  tableRadius: number,
  maxRadius: number,
  startAngle: number,
  endAngle: number
): Vector2Tuple => {
  // normalise angles to be between 0-360 degrees or 0-TAU radians (-90 or 450 => 270)
  startAngle = (startAngle + TAU) % TAU;
  endAngle = (endAngle + TAU) % TAU;

  const box = cabaretBoundingBox(tableRadius, maxRadius, startAngle, endAngle);
  const size = box.getSize(_size);

  return [-(box.min.x + size.x / 2), -(box.min.y + size.y / 2)];
};

export class FArrangementCabaret extends FArrangementGrid<
  FArrangementCabaretSchema,
  FArrangementGridValues,
  FEnsembleCabaretSchema,
  FEnsembleCabaretValues
> {
  usesEnsembles: boolean = true;

  constructor(schema: FArrangementCabaretSchema, initialValues?: FArrangementCabaretAttributes) {
    super(schema, {
      size: [4, 4],
      chairSize: [0, 0],
      tableRadius: 0,
      maxChairsPerTable: 0,
      ...(initialValues as any),
    });
    this.setValue('getMaxElements', this.getMaxElements);
  }

  getMaxElements = () => {
    const gridElements = this.getMaxGridElements();
    const chairsPerTable = this.getValue('chairsPerTable');
    return gridElements * chairsPerTable;
  };

  getNodeTypes() {
    const { chairType, tableType } = this.getValues();

    if (!chairType) {
      throw new Error('chairType is required');
    }

    if (!tableType) {
      throw new Error('tableType is required');
    }

    return [chairType, tableType];
  }

  updateObjectMeasurements() {
    const [chair, table] = this.getObjects();

    if (!chair) {
      throw new Error('chair table object');
    }

    if (!table) {
      throw new Error('missing table object');
    }

    const { chairDistanceFromTable, chairFanRadius, chairsGap } = this.getValues();

    const tableBoundingBox = bbox.setFromObject(table);
    const tableSize = tableBoundingBox.getSize(new Vector3());
    const tableRadius = Math.max(tableSize.x, tableSize.z) / 2;

    const chairBoundingBox = bbox.setFromObject(chair);
    const chairSize = chairBoundingBox.getSize(new Vector3());
    const chairRadius = chairSize.z / 2;

    // distance of chair center from the table center
    const distanceFromCenter = tableRadius + chairRadius * chairDistanceFromTable;

    const chairWidth = chairSize.x;
    const chairEdgeFromCenter = distanceFromCenter - chairRadius;

    const chairAngle = 2 * Math.atan(chairWidth / (2 * chairEdgeFromCenter));
    const chairCornerFromCenter = Math.sqrt(
      (chairWidth / 2) * (chairWidth / 2) + chairEdgeFromCenter * chairEdgeFromCenter
    );

    const chairGapAngle = 2 * Math.asin(chairsGap / (2 * chairCornerFromCenter));
    const fanAngle = TAU * chairFanRadius;
    const maxChairsPerTable = Math.floor((fanAngle + chairGapAngle) / (chairAngle + chairGapAngle));

    this.updateValues({
      tableRadius,
      chairSize: [chairSize.x, chairSize.z],
      maxChairsPerTable,
    });

    // size of the table+chair composition
    this.setValue('elementSize', this.getElementSize());
  }

  getElementSize(): Vector2Tuple {
    const {
      chairDistanceFromTable,
      tableRadius,
      chairSize,
      chairFanRadius,
      chairFanOffset,
      chairsPerTable,
    } = this.getValues();

    if (!tableRadius || !chairSize[1]) {
      return [0, 0];
    }

    const fanAngle = TAU * chairFanRadius;
    const angleOffset = TAU * chairFanOffset;

    const { maxRadius, startAngle, endAngle } = this.calcChairAngles(
      tableRadius,
      chairSize,
      chairDistanceFromTable,
      angleOffset,
      fanAngle,
      chairsPerTable
    );

    const box = cabaretBoundingBox(tableRadius, maxRadius, startAngle, endAngle);
    box.getSize(_size);

    return _size.toArray() as Vector2Tuple;
  }

  getMinimumSize(): Vector2Tuple {
    const { tableRadius, chairSize, chairDistanceFromTable } = this.getValues();
    const chairRadius = chairSize[1] / 2;
    const radiusToBackEdgeOfChair =
      tableRadius + chairRadius * chairDistanceFromTable + chairRadius;
    const maxRadius = Math.hypot(radiusToBackEdgeOfChair, chairSize[0] / 2);
    return [maxRadius * 2, maxRadius * 2] as Vector2Tuple;
  }

  getMaxIterations(maxColumns: number, maxRows: number, ignoreMaxElements: boolean = false) {
    let { elements, chairsPerTable } = this.getValues();
    return this.getSafeIterations(
      Math.min(
        ignoreMaxElements ? Infinity : Math.ceil(elements / chairsPerTable),
        maxColumns * maxRows
      )
    );
  }

  private calcChairAngles(
    tableRadius: number,
    chairSize: Vector2Tuple,
    chairDistanceFromTable: number,
    angleOffset: number,
    fanAngle: number,
    chairsPerTable: number
  ): {
    // distance of chair center from the table center
    distanceFromCenter: number;
    // distance of back corner of the chair from table center
    maxRadius: number;
    // angle at which first chair's left front edge lies
    startAngle: number;
    // angle at which last chair's right front edge lies
    endAngle: number;
  } {
    const chairRadius = chairSize[1] / 2;
    const distanceFromCenter = tableRadius + chairRadius * chairDistanceFromTable;

    const chairWidth = chairSize[0];
    const chairEdgeFromCenter = distanceFromCenter - chairRadius;

    // angle made at the center of the table by the front edge of the chair
    const chairAngle = 2 * Math.atan(chairWidth / (2 * chairEdgeFromCenter));

    const stepAngle = fanAngle / chairsPerTable;

    // calculate radius to one of back corner of the chair
    const radiusToBackEdgeOfChair =
      tableRadius + chairRadius * chairDistanceFromTable + chairRadius;
    const maxRadius = Math.hypot(radiusToBackEdgeOfChair, chairSize[0] / 2);
    const startAngle = angleOffset - fanAngle / 2 + stepAngle / 2 - chairAngle / 2;
    const endAngle = startAngle + (chairsPerTable - 1) * stepAngle + chairAngle;

    return {
      distanceFromCenter,
      maxRadius,
      startAngle,
      endAngle,
    };
  }

  protected getCompositionNodes(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ): FInstanceNode[] {
    let {
      elements,
      tableType,
      chairType,
      chairsPerTable,
      chairFanOffset,
      chairFanRadius,
      tableRadius,
      chairSize,
      chairDistanceFromTable,
      chairsOrientation,
    } = this.getValues();

    const nodes: FInstanceNode[] = [];
    const requiredChairsPerTable = Math.min(elements - idx * chairsPerTable, chairsPerTable);
    const maxIterations = this.getSafeIterations(requiredChairsPerTable);

    const fanAngle = TAU * chairFanRadius;
    const angleOffset = TAU * chairFanOffset;

    const stepAngle = fanAngle / requiredChairsPerTable;

    const { distanceFromCenter, maxRadius, startAngle, endAngle } = this.calcChairAngles(
      tableRadius,
      chairSize,
      chairDistanceFromTable,
      angleOffset,
      fanAngle,
      requiredChairsPerTable
    );

    const orientationOffset = angleForChairsOrientation(chairsOrientation);

    const tPos = getTablePosition(tableRadius, maxRadius, startAngle, endAngle);

    for (let i = 0; i < maxIterations; i++) {
      const theta = i * stepAngle + stepAngle / 2 - fanAngle / 2;

      const chairPosition = [
        tPos[0] + distanceFromCenter * Math.cos(theta + angleOffset),
        tPos[1] + distanceFromCenter * Math.sin(theta + angleOffset),
      ] as Vector2Tuple;

      const chairRotation =
        Math.PI / 2 -
        angleOffset -
        (chairsOrientation === ChairsOrientation.Center ? theta : orientationOffset);

      nodes.push({
        type: 'chair',
        id: chairType,
        position: this.addVector2Tuples(chairPosition, params.position),
        rotation: chairRotation,
      });
    }

    nodes.push({
      type: 'table',
      id: tableType,
      position: this.addVector2Tuples(tPos, params.position),
      rotation: 0,
    });

    return nodes;
  }

  addVector2Tuples(...points: Vector2Tuple[]): Vector2Tuple {
    return points.reduce((sum, p) => [sum[0] + p[0], sum[1] + p[1]], [0, 0]) as Vector2Tuple;
  }

  getEnsembles(
    params: {
      position: Vector2Tuple;
      row: number;
      column: number;
    },
    idx: number
  ): FInstanceEnsembleNode[] {
    let { elements, tableType, chairType, chairsPerTable, chairFanOffset } = this.getValues();

    const requiredChairsPerTable = Math.min(elements - idx * chairsPerTable, chairsPerTable);
    return [
      {
        type: FEnsembleTypesEnum.Cabaret,
        position: [...params.position],
        rotation: 0,
        attrs: {
          chairFanOffset,
          tableType,
          chairType,
          chairsPerTable: requiredChairsPerTable,
        },
      },
    ];
  }
}
